import { useMemo } from "react";
import type { Bar, Trade, Position } from "../types";
import { price, utcShort, sideLabel } from "../format";

interface Props {
  bars: Bar[];
  trades: Trade[];
  position?: Position | null;
  label: string;
  height?: number;
}

const UP = "#16a34a";       // bullish bar
const DOWN = "#e0245e";     // bearish bar
const ENTRY = "#2563eb";    // blue — entries
const EXIT = "#dc2626";     // red — exits
const W = 1000;

type Marker = {
  x: number; yPrice: number; color: string; dir: "up" | "down";
  kind: "entry" | "exit"; title: string;
};

/**
 * Price chart: OHLC bars with trade markers.
 *   Entry = blue arrow; Exit = red arrow.
 *   Long  -> entry points UP, exit points DOWN.
 *   Short -> entry points DOWN, exit points UP (inverse).
 * Hand-rolled SVG so the bars and arrows are exact. Accessible via an
 * aria summary + a screen-reader-only trades table.
 */
export default function PriceChart({ bars, trades, position, label, height = 360 }: Props) {
  const view = useMemo(() => {
    if (!bars || bars.length === 0) return null;
    const padL = 56, padR = 12, padT = 14, padB = 22;
    const n = bars.length;
    const lo = Math.min(...bars.map((b) => b.l));
    const hi = Math.max(...bars.map((b) => b.h));
    const span = hi - lo || 1;
    const yLo = lo - span * 0.06, yHi = hi + span * 0.06;
    const chartW = W - padL - padR, chartH = height - padT - padB;
    const xAt = (i: number) => padL + (n <= 1 ? chartW / 2 : (i / (n - 1)) * chartW);
    const yAt = (p: number) => padT + (1 - (p - yLo) / (yHi - yLo)) * chartH;
    const cw = Math.max(1.2, Math.min(7, (chartW / n) * 0.6));

    // map bar time(ms) -> index for marker placement
    const times = bars.map((b) => Date.parse(b.t));
    const nearest = (iso?: string) => {
      if (!iso) return -1;
      const t = Date.parse(iso);
      let best = 0, bd = Infinity;
      for (let i = 0; i < times.length; i++) {
        const d = Math.abs(times[i] - t);
        if (d < bd) { bd = d; best = i; }
      }
      // ignore markers far outside the visible window
      if (t < times[0] - 6e7 || t > times[n - 1] + 6e7) return -1;
      return best;
    };

    const markers: Marker[] = [];
    for (const tr of trades) {
      const long = sideLabel(tr.side) === "Long";
      const ei = nearest(tr.entry_time);
      if (ei >= 0)
        markers.push({
          x: xAt(ei), yPrice: yAt(tr.entry), color: ENTRY,
          dir: long ? "up" : "down", kind: "entry",
          title: `Entry ${sideLabel(tr.side)} @ ${price(tr.entry)} — ${utcShort(tr.entry_time)}`,
        });
      const xi = nearest(tr.exit_time);
      if (xi >= 0)
        markers.push({
          x: xAt(xi), yPrice: yAt(tr.exit), color: EXIT,
          dir: long ? "down" : "up", kind: "exit",
          title: `Exit ${sideLabel(tr.side)} @ ${price(tr.exit)} — ${tr.exit_reason} — ${utcShort(tr.exit_time)}`,
        });
    }
    // open position entry marker
    if (position) {
      const long = (position.side ?? 0) > 0;
      const pi = nearest(position.entry_time);
      if (pi >= 0)
        markers.push({
          x: xAt(pi), yPrice: yAt(position.entry_price), color: ENTRY,
          dir: long ? "up" : "down", kind: "entry",
          title: `Open ${long ? "Long" : "Short"} @ ${price(position.entry_price)} (current position)`,
        });
    }

    // y gridlines (5)
    const grid = Array.from({ length: 5 }, (_, i) => {
      const p = yLo + ((yHi - yLo) * i) / 4;
      return { p, y: yAt(p) };
    });

    return { bars, padL, padR, padT, padB, chartH, xAt, yAt, cw, markers, grid, n };
  }, [bars, trades, position, height]);

  if (!view) return <p className="muted">No price bars yet for {label}.</p>;

  const { markers, grid, xAt, yAt, cw } = view;
  const last = bars[bars.length - 1];
  const summary =
    `${label}: ${bars.length} bars, last close ${price(last.c)}. ` +
    `${markers.filter((m) => m.kind === "entry").length} entries (blue), ` +
    `${markers.filter((m) => m.kind === "exit").length} exits (red).`;

  // arrow path centred at (x, yTip); points up or down; size s
  const arrow = (x: number, yPrice: number, dir: "up" | "down", color: string, key: string, title: string) => {
    const s = 7, gap = 4;
    // place the arrow just off the price, pointing toward it
    const tipY = dir === "up" ? yPrice + gap : yPrice - gap;
    const baseY = dir === "up" ? tipY + s : tipY - s;
    return (
      <g key={key}>
        <title>{title}</title>
        <line x1={x} y1={yPrice} x2={x} y2={baseY} stroke={color} strokeWidth={1.4} />
        <polygon
          points={`${x},${tipY} ${x - s * 0.7},${baseY} ${x + s * 0.7},${baseY}`}
          fill={color}
        />
      </g>
    );
  };

  return (
    <figure className="chart price-chart" role="group" aria-label={`Price chart for ${label}`}>
      <p className="chart-summary">{summary}</p>
      <div className="chart-legend" aria-hidden="true">
        <span><i className="swatch" style={{ background: UP }} /> up bar</span>
        <span><i className="swatch" style={{ background: DOWN }} /> down bar</span>
        <span><i className="swatch" style={{ background: ENTRY }} /> ▲ entry</span>
        <span><i className="swatch" style={{ background: EXIT }} /> ▼ exit</span>
      </div>
      <div aria-hidden="true">
        <svg viewBox={`0 0 ${W} ${height}`} width="100%" height={height} preserveAspectRatio="xMidYMid meet" className="price-svg">
          {grid.map((g, i) => (
            <g key={i}>
              <line x1={view.padL} y1={g.y} x2={W - view.padR} y2={g.y} stroke="var(--grid)" strokeWidth={1} />
              <text x={view.padL - 6} y={g.y + 3} textAnchor="end" fontSize={10} fill="var(--muted)">{price(g.p)}</text>
            </g>
          ))}
          {bars.map((b, i) => {
            const x = xAt(i);
            const up = b.c >= b.o;
            const col = up ? UP : DOWN;
            return (
              <g key={i} stroke={col} strokeWidth={1.1}>
                <line x1={x} y1={yAt(b.h)} x2={x} y2={yAt(b.l)} />
                <line x1={x - cw} y1={yAt(b.o)} x2={x} y2={yAt(b.o)} />
                <line x1={x} y1={yAt(b.c)} x2={x + cw} y2={yAt(b.c)} />
              </g>
            );
          })}
          {markers.map((m, i) => arrow(m.x, m.yPrice, m.dir, m.color, `m${i}`, m.title))}
        </svg>
      </div>
      <table className="visually-hidden">
        <caption>Trade markers for {label}</caption>
        <thead><tr><th scope="col">Type</th><th scope="col">Side</th><th scope="col">Price</th></tr></thead>
        <tbody>
          {markers.map((m, i) => (
            <tr key={i}><td>{m.kind}</td><td>{m.dir === "up" ? "up arrow" : "down arrow"}</td><td>{m.title}</td></tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
